import * as PIXI from 'pixi.js';
import { TowerType } from '../types/GameTypes';
import { GAME_CONSTANTS } from '../utils/Constants';
import { TOWER_STATS } from '../types/TowerTypes';

export class BuildMenu {
  private container: PIXI.Container;
  private buttons: Map<TowerType, PIXI.Graphics> = new Map();
  private onBuildCallback?: (type: TowerType) => void;
  private selectedType: TowerType | null = null;

  // Building display names
  private buildingNames: Record<TowerType | 'wall', string> = {
    arrow: 'Arrow',
    cannon: 'Cannon',
    sniper: 'Sniper',
    fire: 'Fire',
    ice: 'Ice',
    lightning: 'Lightning',
    wall: 'Wall',
  };

  constructor() {
    this.container = new PIXI.Container();
    this.setupMenu();
  }

  private setupMenu(): void {
    const towerTypes: TowerType[] = ['arrow', 'cannon', 'sniper'];
    // Position at bottom center - centered under the 20x20 map
    // Map is at x=640, width=640, so center build menu at x=640
    // Build menu with 4 buttons (3 towers + wall) = 4 * 70 = 280px, so start at 640 - 140 = 500
    // Arena ends at y=860 (220 + 640), position buttons just below at y=870
    const startX = 500;
    const startY = 870; // Just below the arena (arena ends at y=860)
    const buttonSize = 60;
    const buttonHeight = buttonSize + 25; // Extra space for name
    const spacing = 70;

    towerTypes.forEach((type, index) => {
      const button = new PIXI.Graphics();
      const cost = TOWER_STATS[type].cost;
      
      button.beginFill(0x444444, 0.3); // Transparent background
      button.drawRect(0, 0, buttonSize, buttonHeight);
      button.endFill();
      button.lineStyle(2, 0x888888, 0.7); // Semi-transparent border
      button.drawRect(0, 0, buttonSize, buttonHeight);
      
      button.x = startX + index * spacing;
      button.y = startY;

      // Tower icon - triangle with color based on type
      const icon = new PIXI.Graphics();
      let iconColor = 0x87ceeb; // Light blue for arrow (default)
      if (type === 'arrow') iconColor = 0x87ceeb; // Light blue
      else if (type === 'cannon') iconColor = 0x800080; // Purple
      else if (type === 'sniper') iconColor = 0xffffff; // White
      
      icon.beginFill(iconColor);
      const centerX = buttonSize / 2;
      const centerY = buttonSize / 2;
      const size = 15;
      icon.moveTo(centerX, centerY - size);
      icon.lineTo(centerX - size, centerY + size);
      icon.lineTo(centerX + size, centerY + size);
      icon.lineTo(centerX, centerY - size);
      icon.endFill();
      button.addChild(icon);

      // Cost text
      const costText = new PIXI.Text(`${cost}g`, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xffd700,
        fontWeight: 'bold',
      });
      costText.x = buttonSize / 2 - costText.width / 2;
      costText.y = buttonSize - 15;
      button.addChild(costText);

      // Tower name under cost
      const nameText = new PIXI.Text(this.buildingNames[type], {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xffffff,
      });
      nameText.x = buttonSize / 2 - nameText.width / 2;
      nameText.y = buttonSize + 2;
      button.addChild(nameText);

      // Make interactive
      button.eventMode = 'static';
      button.cursor = 'pointer';
      button.on('pointerdown', () => {
        // Toggle off if already selected
        if (this.selectedType === type) {
          this.selectType(null);
          if (this.onBuildCallback) {
            this.onBuildCallback(null as any);
          }
        } else {
        this.selectType(type);
        if (this.onBuildCallback) {
          this.onBuildCallback(type);
          }
        }
      });

      this.buttons.set(type, button);
      this.container.addChild(button);
    });

    // Wall button - positioned after tower buttons
    const wallButton = new PIXI.Graphics();
    wallButton.beginFill(0x444444, 0.3); // Transparent background
    wallButton.drawRect(0, 0, buttonSize, buttonHeight);
    wallButton.endFill();
    wallButton.lineStyle(2, 0x888888, 0.7); // Semi-transparent border
    wallButton.drawRect(0, 0, buttonSize, buttonHeight);
    wallButton.x = startX + towerTypes.length * spacing; // Use startX, not hardcoded 10
    wallButton.y = startY;

    const wallIcon = new PIXI.Graphics();
    wallIcon.beginFill(0x888888);
    wallIcon.drawRect(10, 10, buttonSize - 20, buttonSize - 20);
    wallIcon.endFill();
    wallButton.addChild(wallIcon);

    const wallCostText = new PIXI.Text(`${GAME_CONSTANTS.WALL_COST}g`, {
      fontFamily: 'Courier New',
      fontSize: 16,
      fill: 0xffd700,
      fontWeight: 'bold',
    });
    wallCostText.x = buttonSize / 2 - wallCostText.width / 2;
    wallCostText.y = buttonSize - 15;
    wallButton.addChild(wallCostText);

    // Wall name under cost
    const wallNameText = new PIXI.Text(this.buildingNames.wall, {
      fontFamily: 'Courier New',
      fontSize: 13,
      fill: 0xffffff,
    });
    wallNameText.x = buttonSize / 2 - wallNameText.width / 2;
    wallNameText.y = buttonSize + 2;
    wallButton.addChild(wallNameText);

    wallButton.eventMode = 'static';
    wallButton.cursor = 'pointer';
    wallButton.on('pointerdown', () => {
      // Toggle off if already selected
      if (this.selectedType === 'wall' as any) {
        this.selectType(null);
        if (this.onBuildCallback) {
          this.onBuildCallback(null as any);
        }
      } else {
      this.selectType('wall' as any);
      if (this.onBuildCallback) {
        this.onBuildCallback('wall' as any);
        }
      }
    });

    this.buttons.set('wall' as any, wallButton);
    this.container.addChild(wallButton);
  }

  selectType(type: TowerType | 'wall' | null): void {
    const buttonSize = 60;
    const buttonHeight = buttonSize + 25;
    
    // Deselect previous
    if (this.selectedType) {
      const prevButton = this.buttons.get(this.selectedType);
      if (prevButton) {
        prevButton.clear();
        prevButton.beginFill(0x444444, 0.3); // Transparent background
        prevButton.drawRect(0, 0, buttonSize, buttonHeight);
        prevButton.endFill();
        prevButton.lineStyle(2, 0x888888, 0.7); // Semi-transparent border
        prevButton.drawRect(0, 0, buttonSize, buttonHeight);
        // Re-add children (icon, cost, name)
        this.rebuildButtonChildren(prevButton, this.selectedType);
      }
    }

    if (type === null) {
      this.selectedType = null;
      return;
    }

    this.selectedType = type as TowerType;
    
    // Highlight selected
    const button = this.buttons.get(this.selectedType);
    if (button) {
      button.clear();
      button.beginFill(0x666666, 0.5); // Slightly more visible when selected, but still transparent
      button.drawRect(0, 0, buttonSize, buttonHeight);
      button.endFill();
      button.lineStyle(3, 0x00ff00, 0.9); // Bright green border when selected
      button.drawRect(0, 0, buttonSize, buttonHeight);
      // Re-add children (icon, cost, name)
      this.rebuildButtonChildren(button, this.selectedType);
    }
  }

  private rebuildButtonChildren(button: PIXI.Graphics, type: TowerType | 'wall'): void {
    const buttonSize = 60;
    
    if (type === 'wall') {
      const wallIcon = new PIXI.Graphics();
      wallIcon.beginFill(0x888888);
      wallIcon.drawRect(10, 10, buttonSize - 20, buttonSize - 20);
      wallIcon.endFill();
      button.addChild(wallIcon);

      const wallCostText = new PIXI.Text(`${GAME_CONSTANTS.WALL_COST}g`, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xffd700,
        fontWeight: 'bold',
      });
      wallCostText.x = buttonSize / 2 - wallCostText.width / 2;
      wallCostText.y = buttonSize - 15;
      button.addChild(wallCostText);

      const wallNameText = new PIXI.Text(this.buildingNames.wall, {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xffffff,
      });
      wallNameText.x = buttonSize / 2 - wallNameText.width / 2;
      wallNameText.y = buttonSize + 2;
      button.addChild(wallNameText);
    } else {
      const cost = TOWER_STATS[type].cost;
      
      const icon = new PIXI.Graphics();
      let iconColor = 0x87ceeb; // Light blue for arrow (default)
      if (type === 'arrow') iconColor = 0x87ceeb; // Light blue
      else if (type === 'cannon') iconColor = 0x800080; // Purple
      else if (type === 'sniper') iconColor = 0xffffff; // White
      
      icon.beginFill(iconColor);
      const centerX = buttonSize / 2;
      const centerY = buttonSize / 2;
      const size = 15;
      icon.moveTo(centerX, centerY - size);
      icon.lineTo(centerX - size, centerY + size);
      icon.lineTo(centerX + size, centerY + size);
      icon.lineTo(centerX, centerY - size);
      icon.endFill();
      button.addChild(icon);

      const costText = new PIXI.Text(`${cost}g`, {
        fontFamily: 'Courier New',
        fontSize: 16,
        fill: 0xffd700,
        fontWeight: 'bold',
      });
      costText.x = buttonSize / 2 - costText.width / 2;
      costText.y = buttonSize - 15;
      button.addChild(costText);

      const nameText = new PIXI.Text(this.buildingNames[type], {
        fontFamily: 'Courier New',
        fontSize: 13,
        fill: 0xffffff,
      });
      nameText.x = buttonSize / 2 - nameText.width / 2;
      nameText.y = buttonSize + 2;
      button.addChild(nameText);
    }
  }

  onBuild(callback: (type: TowerType | 'wall' | null) => void): void {
    this.onBuildCallback = callback;
  }

  getContainer(): PIXI.Container {
    return this.container;
  }

  getSelectedType(): TowerType | null {
    return this.selectedType;
  }
}

